import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Flag } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { restoreItem } from '../lib/db'
import { Banner, Button, Field, Row, Segmented, Sheet, Tag, type SwipeAction, useToast } from '../ui'
import { useAddItem, useRemoveItem, useSetItemFields, useUpdateItem } from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { addPlain } from '../lib/listAdd'
import { byAddedDesc } from '../lib/newPosition'
import { useFeed } from '../lib/queries'
import { heroIsVenue, heroUrl, posterUrl } from '../lib/entries'
import { leadingOf, metaLine, plateFor, rowTitle } from '../lib/rowMeta'
import { EventSheet } from './EventSheet'
import { RowMore } from './RowMore'
import { shopOf } from '../lib/listSummary'
import { useLinger } from '../lib/useLinger'
import { finishedHead, foldFinished } from '../lib/finished'
import type { FeedRow, List, ListItem } from '../lib/types'
import './EventRow.css'

type Sort = 'az' | 'added' | 'flagged'

const SEG_OPTIONS: { value: Sort; label: string }[] = [
  { value: 'az', label: 'A–Z' },
  { value: 'added', label: 'Added' },
  { value: 'flagged', label: 'Flagged' },
]

const capitalize = (s: string) => s.length ? s[0].toUpperCase() + s.slice(1) : s

/**
 * L7: a collection — no checkbox, a flag per kind, a note. Split out of
 * `ListDetail.tsx` the way `GroceryList` and `TaskList` were before it.
 *
 * The cross-link a collection row could show ("Objekt is at Berghain on
 * Saturday") is not built here — it needs a matcher between an item's plain
 * text and the feed, which belongs to the data plan, not this package. Left
 * as a note for whoever next reads this file with that matcher in hand.
 */
export function CollectionList({ list, items }: { list: List; items: ListItem[] }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [sort, setSort] = useState<Sort>('az')
  // The after-checked state (`lib/finished.ts`): in A–Z and Added, a flagged
  // row — seen, visited, read — leaves the rows still waiting for a fold at
  // the bottom, newest first, closed until opened. A flagged row lingers in
  // place first, its tag lit, then collapses out; a seen show is not "done",
  // so there is no strike-through. The Flagged view is the fold on its own.
  const [foldOpen, setFoldOpen] = useState(false)
  const linger = useLinger()
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [edit, setEdit] = useState<ListItem | null>(null)
  // L8: which event's sheet is open, from tapping an item that carries an
  // `entry_id` — the item editor is not reached from that tap.
  const [openEntry, setOpenEntry] = useState<FeedRow | null>(null)

  const addM = useAddItem()
  const removeM = useRemoveItem()
  const updateM = useUpdateItem()
  const fieldsM = useSetItemFields()
  const error = useWriteError(addM, removeM, updateM, fieldsM)
  const busy = addM.isPending

  const feedQ = useFeed()
  const feedById = useMemo(() => new Map((feedQ.data ?? []).map(e => [e.id, e])), [feedQ.data])
  const entryFor = (i: ListItem): FeedRow | null => (i.entry_id ? feedById.get(i.entry_id) ?? null : null)

  const flagLabel = list.flag_label

  const trimmed = query.trim().toLowerCase()
  const searched = trimmed
    ? items.filter(i => i.body.toLowerCase().includes(trimmed) || (i.note ?? '').toLowerCase().includes(trimmed))
    : items

  let shown: ListItem[]
  let folded: ListItem[] = []
  if (sort === 'flagged') shown = searched.filter(i => i.flagged_at).sort((a, b) => (a.flagged_at! < b.flagged_at! ? 1 : -1))
  else {
    const f = foldFinished('collection', searched, linger.lingering)
    shown = sort === 'az' ? [...f.open].sort((a, b) => a.body.localeCompare(b.body)) : [...f.open].sort(byAddedDesc)
    folded = f.finished
  }
  useEffect(() => { if (fieldsM.isError) linger.cancelAll() }, [fieldsM.isError, linger])

  // a2-22: one layout, always. There used to be a two-column grid of names
  // for a collection with no notes, which flipped to rows the moment one note
  // was added anywhere — the list changed shape under the hand that changed
  // it. Rows carry the note and the flag; a name-only card carried neither.

  async function add(text: string) {
    if (!text.trim()) return
    const out = await addPlain({ addM }, list, items, text)
    if (out.ok && !out.empty) setDraft('')
  }

  // F7: removal is a swipe and a toast, never a confirmation dialog — the same
  // shape `ListDetail.tsx`'s own `restore` is, with this kind's own columns.
  async function restore(i: ListItem, gone: Promise<unknown>) {
    const { error: err } = await restoreItem(i, gone)
    if (!err) void qc.invalidateQueries({ queryKey: ['list_items'] })
  }

  const del = (i: ListItem) => {
    // The delete's own promise, so Undo writes the row back after it and
    // not before (a2-14): the row returns under its own id now, which a
    // delete still in flight would otherwise remove a second time.
    const gone = removeM.mutateAsync(i.id).catch(() => {})
    toast({ text: `Removed "${i.body}"`, action: { label: 'Undo', onClick: () => void restore(i, gone) } })
  }

  /** Rule 2: the flag toggle has Undo, same as every other write here.
   *  Reached from the row's own flag, its swipe, its "…" and the edit
   *  sheet's flag row. */
  function toggleFlag(i: ListItem) {
    const prev = i.flagged_at
    const next = prev ? null : new Date().toISOString()
    fieldsM.mutate({ id: i.id, patch: { flagged_at: next } })
    if (next) linger.start(i.id); else linger.cancel(i.id)
    toast({
      text: next ? `Marked "${i.body}" ${flagLabel}` : `Removed the flag from "${i.body}"`,
      action: { label: 'Undo', onClick: () => { linger.cancel(i.id); fieldsM.mutate({ id: i.id, patch: { flagged_at: prev } }) } },
    })
  }

  return (
    <>
      {error && <Banner tone="bad">{error}</Banner>}

      <div className="lists-segscroll">
        <Segmented value={sort} onChange={v => setSort(v as Sort)} options={SEG_OPTIONS} />
      </div>

      {items.length > 20 && (
        <form className="lists-search" role="search" onSubmit={ev => ev.preventDefault()}>
          <Field.Input
            type="search" value={query} onChange={ev => setQuery(ev.currentTarget.value)}
            aria-label={`Search ${list.name}`} placeholder="Search"
          />
        </form>
      )}

      <div className="lists-addrow">
        <Field label="Add" className="lists-addrow__field">
          <Field.Input
            value={draft} placeholder="Add…" autoComplete="off"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void add(draft) }}
          />
        </Field>
        <Button variant="primary" disabled={busy || !draft.trim()} onClick={() => void add(draft)}>Add</Button>
      </div>

      {!shown.length && !folded.length ? (
        <div className="stub">{sort === 'flagged' ? `Nothing ${flagLabel ?? 'flagged'} yet.` : 'Nothing here yet.'}</div>
      ) : (
        <div className="lists-group">
          {shown.map(i => (
            <CollectionRow
              key={i.id} item={i} flagLabel={flagLabel} entry={entryFor(i)} onOpenEvent={setOpenEntry}
              leaving={linger.leaving.has(i.id)}
              onOpen={() => setEdit(i)} onToggleFlag={() => toggleFlag(i)} onDelete={() => del(i)}
            />
          ))}
        </div>
      )}

      {folded.length > 0 && (
        <>
          <button type="button" className="lists-grouphead collection__foldhead" aria-expanded={foldOpen} onClick={() => setFoldOpen(v => !v)}>
            <span>{finishedHead('collection', list, folded.length)}</span>
          </button>
          {foldOpen && (
            <div className="lists-group collection__fold">
              {folded.map(i => (
                <CollectionRow
                  key={i.id} item={i} flagLabel={flagLabel} entry={entryFor(i)} onOpenEvent={setOpenEntry}
                  onOpen={() => setEdit(i)} onToggleFlag={() => toggleFlag(i)} onDelete={() => del(i)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {edit && (
        <CollectionEditSheet
          item={edit} flagLabel={flagLabel}
          onClose={() => setEdit(null)}
          onToggleFlag={() => toggleFlag(edit)}
          onSave={patch => {
            const id = edit.id
            setEdit(null)
            updateM.mutate({ id, patch: { body: patch.body, link: patch.link } })
            fieldsM.mutate({ id, patch: { note: patch.note } })
          }}
        />
      )}

      <EventSheet e={openEntry} onClose={() => setOpenEntry(null)} />
    </>
  )
}

function CollectionRow({ item, flagLabel, entry, onOpenEvent, leaving, onOpen, onToggleFlag, onDelete }: {
  item: ListItem; flagLabel: string | null
  /** The linger's last act: the row collapses out, on its way to the fold. */
  leaving?: boolean
  /** L8: the feed row this item was added from, or null once it has left the
   *  feed — see `TaskList.tsx`'s own note on the same field. */
  entry?: FeedRow | null
  onOpenEvent?: (e: FeedRow) => void
  onOpen: () => void; onToggleFlag: () => void; onDelete: () => void
}) {
  const flagAction = item.flagged_at ? `Remove the ${flagLabel} flag` : `Mark ${flagLabel}`
  const swipe = {
    left: { label: 'Remove', onAction: onDelete, leaves: true } as SwipeAction,
    ...(flagLabel
      ? { right: { label: item.flagged_at ? 'Remove flag' : `Mark ${flagLabel}`, onAction: onToggleFlag } as SwipeAction }
      : {}),
  }

  // a2-22, Doug chose: every row shows its flag, always — accent when set,
  // neutral when not, and a tap toggles it (with Undo, `toggleFlag`). It is
  // the trailing control on a text row exactly as it already was on an
  // event row (L8), so the two kinds of item no longer disagree about where
  // the flag lives.
  const flagBtn = flagLabel ? (
    <button type="button" className="collection__flagbtn" onClick={onToggleFlag}
      aria-label={item.flagged_at ? `Remove the ${flagLabel} flag from "${item.body}"` : `Mark "${item.body}" ${flagLabel}`}
      aria-pressed={!!item.flagged_at}>
      <Tag tone={item.flagged_at ? 'accent' : 'neutral'}>{capitalize(flagLabel)}</Tag>
    </button>
  ) : null
  // a2-32: Edit, the flag and Remove behind a "…" you can see. An event item
  // has no Edit — its tap opens the event.
  const more = (canEdit: boolean) => (
    <RowMore
      name={item.body} onEdit={canEdit ? onOpen : undefined} onRemove={onDelete}
      extra={flagLabel ? [{ label: flagAction, icon: <Flag size={20} aria-hidden="true" />, onSelect: onToggleFlag }] : []}
    />
  )
  const trailing = (canEdit: boolean) => <>{flagBtn}{more(canEdit)}</>

  // L8: see `TaskList.tsx`'s own note on the same shape.
  if (item.entry_id) {
    if (entry) return <EventItemRow entry={entry} trailing={trailing(false)} swipe={swipe} onOpen={() => onOpenEvent?.(entry)} />
    return (
      <Row
        className="u-row--list u-row--flush collection__row"
        leaving={leaving}
        leading={null}
        title={<span className="lists-itembody">{item.body}</span>}
        meta={<Tag>Passed</Tag>}
        trailing={trailing(false)}
        swipe={swipe}
      />
    )
  }

  // The note on the second line, always where there is one; a row with no
  // note but a link names the link's site there instead, which is what the
  // trailing link glyph used to stand for with no word.
  return (
    <Row
      className="u-row--list u-row--flush collection__row"
      leaving={leaving}
      leading={null}
      title={<button type="button" className="lists-itembody" onClick={onOpen}>{item.body}</button>}
      meta={item.note || shopOf(item.link) || null}
      trailing={trailing(true)}
      swipe={swipe}
    />
  )
}

function CollectionEditSheet({ item, flagLabel, onClose, onToggleFlag, onSave }: {
  item: ListItem; flagLabel: string | null
  onClose: () => void; onToggleFlag: () => void
  onSave: (patch: { body: string; link: string | null; note: string | null }) => void
}) {
  const [body, setBody] = useState(item.body)
  const [link, setLink] = useState(item.link ?? '')
  const [note, setNote] = useState(item.note ?? '')
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Edit item"
      actions={
        <>
          <Button variant="primary" disabled={!body.trim()}
            onClick={() => onSave({ body: body.trim(), link: link.trim() || null, note: note.trim() || null })}>
            Save
          </Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <div className="lists-form">
        <Field label="Item"><Field.Input value={body} autoFocus onChange={e => setBody(e.target.value)} /></Field>
        <Field label="Note"><Field.Input value={note} onChange={e => setNote(e.target.value)} /></Field>
        <Field label="Link">
          <Field.Input value={link} placeholder="https://…" inputMode="url" onChange={e => setLink(e.target.value)} />
        </Field>
        {/* The flag from inside the editor too — this Row, the same anatomy
            `ListMenu.tsx` uses for an action that isn't a form field. */}
        {flagLabel && (
          <Row
            className="u-row--list"
            leading={<Tag tone={item.flagged_at ? 'accent' : 'neutral'}>{capitalize(flagLabel)}</Tag>}
            title={item.flagged_at ? `Remove the ${flagLabel} flag` : `Mark ${flagLabel}`}
            meta={null}
            onPress={onToggleFlag}
          />
        )}
      </div>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// L8: an event, inside a list — the same shape `TaskList.tsx`'s own copy is,
// for the same reason (`Thumb`'s own note there).
// ---------------------------------------------------------------------------

function Thumb({ e }: { e: FeedRow }) {
  const now = new Date()
  const kind = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  return (
    <span className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as { [k: string]: string }) : undefined}>
      {plate && (
        <span className="ev-plate">
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img src={kind.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
      )}
    </span>
  )
}

function EventItemRow({ entry, trailing, swipe, onOpen }: {
  entry: FeedRow
  trailing: ReactNode
  swipe?: { left?: SwipeAction; right?: SwipeAction }
  onOpen: () => void
}) {
  const now = new Date()
  const m = metaLine(entry, { now })
  const rest = [m.venue, m.fact].filter(Boolean).join(' · ')
  return (
    <Row
      leading={<Thumb e={entry} />}
      title={rowTitle(entry)}
      meta={
        <>
          <span className="ev-row__dot" style={{ background: m.dot }} aria-hidden="true" />
          <b className="ev-row__when">{m.when}</b>
          {rest && <span className="ev-row__rest">{rest}</span>}
        </>
      }
      trailing={trailing}
      onPress={onOpen}
      swipe={swipe}
    />
  )
}
